import { ArrowRight, Check, Eye, MousePointer2 } from "lucide-react";
import SlideGuide, { type GuideSlide } from "@/components/training/SlideGuide";
import { S2P_NOVEMBER_SLIDES, S2P_NOVEMBER_STEPS } from "@/data/s2pAIBusinessCouncilNovember";
import "./S2PAIBusinessCouncilNovember.css";

function Implication({ heading, action }: { heading: string; action: string }) {
  return <div className="november-implication">
    <p className="november-implication-label">Why it matters</p>
    <div><h2>{heading}</h2><p>{action}</p></div>
  </div>;
}

function NovemberSlide({ slide }: { slide: GuideSlide }) {
  if (slide.id === "start") return <div className="november-slide november-cover">
    <div className="november-cover-copy">
      <p className="november-eyebrow">AI Business Council · November Update</p>
      <h1>New Risks,<br />Opportunities,<br /><em>&amp; Capabilities</em></h1>
    </div>
    <ol className="november-agenda">
      <li><span>01 / Risk</span><h2>Single-Tool<br />Dependence</h2><p>Keep critical work moving.</p></li>
      <li><span>02 / Opportunity</span><h2>Owned AI<br />vs. Rented AI</h2><p>Decide what to own and operate.</p></li>
      <li><span>03 / Capability</span><h2>Computer Use</h2><p>Delegate routine steps with oversight.</p></li>
    </ol>
  </div>;

  if (slide.id === "single-tool-dependence") return <div className="november-slide november-risk">
    <div className="november-split">
      <div className="november-copy">
        <p className="november-eyebrow">New Risk / Single-Tool Dependence</p>
        <h1>One AI tool.<br /><em>One point of failure.</em></h1>
        <p className="november-lead">Putting all your training, workflows, and company knowledge in one tool raises the cost of an outage.</p>
      </div>
      <div className="november-headlines" aria-label="AI outage reporting">
        <figure className="november-headline">
          <figcaption><strong>ITPro</strong><time dateTime="2026-09-04">Sep 4, 2026</time></figcaption>
          <img src="/s2p-council-itpro-outage-headline.webp" width={920} height={104} alt="ITPro reports overlapping OpenAI, Anthropic and xAI disruptions as a business-continuity concern." />
        </figure>
        <figure className="november-headline">
          <figcaption><strong>The Next Web</strong><time dateTime="2026-09-29">Sep 29, 2026</time></figcaption>
          <img src="/s2p-council-tnw-claude-outage-headline.webp" width={1136} height={124} alt="The Next Web reports errors affecting some Claude users across several services." />
        </figure>
      </div>
    </div>
    <Implication heading="An AI outage can become a business outage." action="Keep critical playbooks portable and a manual path ready." />
  </div>;

  if (slide.id === "owned-vs-rented") return <div className="november-slide november-ownership">
    <div className="november-copy">
      <p className="november-eyebrow">New Opportunity / Owned AI vs. Rented AI</p>
      <h1>Run your own. <em>Or rent the frontier.</em></h1>
    </div>
    <div className="november-comparison">
      <div><span className="november-label">Owned</span><h2>Open-weight models</h2><p>Run on your servers or private cloud.</p><p className="november-tradeoff">More control.<br />You manage compute, security + updates.</p></div>
      <div><span className="november-label">Rented</span><h2>Frontier cloud<br />models</h2><p>Use through a subscription or API.</p><p className="november-tradeoff">Less infrastructure to run.<br />You depend on provider access + uptime.</p></div>
      <figure className="november-headline november-owned-case">
        <figcaption><strong>Legal IT Insider</strong><time dateTime="2026-09-11">Sep 11, 2026</time></figcaption>
        <img src="/s2p-council-latham-owned-ai-headline.webp" width={816} height={92} alt="Latham builds its own AI models with Nvidia GPU server investment" />
        <p className="november-case-purpose">Private infrastructure for sensitive work and pricing flexibility.</p>
        <p className="november-case-savings">Savings not publicly quantified.</p>
        <p className="november-case-cloud">Cloud tools remain in use.</p>
      </figure>
    </div>
    <Implication heading="Where AI runs changes what you control, pay for, and maintain." action="Match each workload to its data needs, capabilities, and total cost." />
  </div>;

  if (slide.id === "computer-use") return <div className="november-slide november-computer">
    <div className="november-copy">
      <p className="november-eyebrow">New Capability / Computer Use</p>
      <h1>AI can work <em>in the interface.</em></h1>
    </div>
    <ol className="november-computer-flow" aria-label="Computer use cycle">
      <li><Eye aria-hidden="true" /><h2>See the screen</h2></li>
      <li><MousePointer2 aria-hidden="true" /><h2>Click + type</h2></li>
      <li><Check aria-hidden="true" /><h2>Check the result</h2></li>
    </ol>
    <ul className="november-examples" aria-label="Computer use examples">
      <li><h3>Monthly reporting</h3><p>Open dashboard <ArrowRight aria-hidden="true" /> download report <ArrowRight aria-hidden="true" /> prepare a draft</p></li>
      <li><h3>Sales follow-up</h3><p>Open CRM <ArrowRight aria-hidden="true" /> review account <ArrowRight aria-hidden="true" /> draft follow-up</p></li>
      <li><h3>Meeting prep</h3><p>Open calendar <ArrowRight aria-hidden="true" /> gather docs <ArrowRight aria-hidden="true" /> draft agenda</p></li>
      <li><h3>Invoice intake</h3><p>Open portal <ArrowRight aria-hidden="true" /> download invoice <ArrowRight aria-hidden="true" /> draft invoice entry</p></li>
    </ul>
    <Implication heading="Once a person teaches the workflow, AI can carry out computer-based tasks." action="Start with one bounded task. Review before sending or changing records." />
  </div>;

  return null;
}

const renderCustomSlide = (slide: GuideSlide) => <NovemberSlide slide={slide} />;

export default function S2PAIBusinessCouncilNovember() {
  return <SlideGuide
    name="Serve2Perform AI Business Council · November"
    trackingName="S2P AI Business Council November"
    canonicalPath="/s2p-ai-business-council-november"
    storageKey="s2p-ai-business-council-november-completed-steps"
    slideStorageKey="s2p-ai-business-council-november-current-slide"
    audienceView
    centerSlideContent
    seo={{ title: "AI Business Council November | Serve2Perform + Technical Leaders", description: "A five-minute leadership briefing on single-tool dependence and AI outages, owned vs. rented AI, and supervised computer use.", keywords: ["Serve2Perform", "AI Business Council", "AI Outages", "Single-Tool Dependence", "Owned AI", "Computer Use"] }}
    schema={{ description: "Three AI shifts for business leaders.", totalTime: "PT5M" }}
    cover={{ title: "New Risks, Opportunities,", accent: "& Capabilities", description: "", badges: [], finishLineTitle: "Choose one bounded task", finishLineBody: "Define the task, limit its access, and review the result." }}
    outcomes={{ eyebrow: "Three shifts", heading: "Risk, opportunity, capability", items: ["Plan for AI outages", "Compare self-hosted open models with frontier cloud models", "Supervise AI working in an interface"], summary: "Choose one bounded task to test." }}
    roadmapHeading="New Risks, Opportunities, & Capabilities"
    steps={S2P_NOVEMBER_STEPS}
    slides={S2P_NOVEMBER_SLIDES}
    contentsExtras={S2P_NOVEMBER_SLIDES.map(({ id, title }) => ({ slideId: id, label: title }))}
    branding={{ rootClassName: "s2p-november", chrome: "light", contentsTitle: "November briefing", logo: { src: "/serve2perform-logo.png", alt: "Serve2Perform", href: "/s2p-ai-business-council-november#start", ariaLabel: "Return to the November AI Business Council opening slide", imageClassName: "h-6 w-auto sm:h-7" }, paperBackdrop: "none" }}
    renderCustomSlide={renderCustomSlide}
    finish={{ eyebrow: "Your next move", heading: "Choose one bounded task to test.", body: "Keep a person accountable for the result.", cards: [] }}
  />;
}
